import React, { useState, useEffect, useMemo } from "react";
import "./DeviceClaimingModal.css";
import { useAuth } from "./contexts/AuthContext";
import { useInstallationId } from "./stores/orgInstallStore";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import config from "./config";
import logger from "./utils/logger";

const DeviceClaimingModal = ({
  isOpen,
  onClose,
  devices,
  onDevicesClaimed,
}) => {
  const [view, setView] = useState("choice"); // 'choice', 'claim', 'unclaim'
  const [claimToken, setClaimToken] = useState("");
  const [decodedToken, setDecodedToken] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { success: boolean, message: string }
  const [typedConfirmation, setTypedConfirmation] = useState("");
  const [autoCloseTimer, setAutoCloseTimer] = useState(null);

  const { getAccessToken } = useAuth();
  const installationId = useInstallationId();

  // Decode JWT in real-time as user types
  useEffect(() => {
    if (!claimToken.trim()) {
      setDecodedToken(null);
      return;
    }

    try {
      const parts = claimToken.trim().split(".");
      if (parts.length !== 3) {
        setDecodedToken({
          error: "Invalid JWT format. Expected 3 parts separated by dots.",
        });
        return;
      }

      // Decode header and payload
      const header = JSON.parse(
        atob(parts[0].replace(/-/g, "+").replace(/_/g, "/")),
      );
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
      );

      setDecodedToken({
        header,
        payload,
        raw: claimToken.trim(),
      });
    } catch (error) {
      setDecodedToken({
        error: "Failed to decode JWT. Please check the token format.",
      });
    }
  }, [claimToken]);

  // Auto-close timer management - only auto-close on success
  useEffect(() => {
    if (result && result.success && autoCloseTimer === null) {
      const timerId = setTimeout(() => {
        handleClose();
      }, 8000);
      setAutoCloseTimer(timerId);
    }

    return () => {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, autoCloseTimer]);

  const handleClose = () => {
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      setAutoCloseTimer(null);
    }
    setView("choice");
    setClaimToken("");
    setDecodedToken(null);
    setSelectedDeviceId("");
    setShowConfirmation(false);
    setTypedConfirmation("");
    setLoading(false);
    setResult(null);
    onClose();
  };

  const handleClaimDevice = async () => {
    if (!claimToken.trim() || !decodedToken || decodedToken.error) {
      setResult({
        success: false,
        message: "Please provide a valid claim token.",
      });
      return;
    }

    setLoading(true);

    try {
      // Refresh access token if needed (within 5 minutes of expiry)
      await getAccessToken();

      const response = await fetch(
        `${config.apiEndpoint}/claim-device?installationId=${installationId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": config.apiKey,
          },
          body: JSON.stringify({ claimToken: claimToken.trim() }),
        },
      );

      const data = await response.json();

      // eslint-disable-next-line no-console -- Intentional: surface claim API responses in browser DevTools
      console.log("Claim device response:", {
        status: response.status,
        ok: response.ok,
        data,
      });

      if (response.ok && data.success) {
        setResult({
          success: true,
          message: `Device claimed successfully! Device ID: ${
            data.deviceId || "Unknown"
          }`,
        });
        if (onDevicesClaimed) {
          onDevicesClaimed();
        }
      } else {
        // eslint-disable-next-line no-console -- Intentional: surface claim errors in browser DevTools
        console.error("Claim device failed:", {
          status: response.status,
          data,
        });

        // Map backend error messages to user-friendly text
        const rawError = data.error || data.message || "";
        let userMessage;
        if (
          rawError.includes("not in factory default state") ||
          rawError.includes("DEVICE_RESET_REQUIRED")
        ) {
          userMessage =
            "This device must be factory reset before it can be claimed. Please reset the device and generate a new claim token.";
        } else if (
          rawError.includes("claimToken") &&
          rawError.includes("expired")
        ) {
          userMessage =
            "The claim token has expired. Please generate a new one and try again.";
        } else if (
          rawError.includes("already claimed") ||
          rawError.includes("already owned")
        ) {
          userMessage =
            "This device is already claimed. Unclaim it first before re-claiming.";
        } else if (rawError) {
          userMessage = rawError;
        } else {
          userMessage = "Failed to claim device. Please try again.";
        }

        setResult({
          success: false,
          message: userMessage,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- Intentional: surface network errors in browser DevTools
      console.error("Claim device network error:", error);
      logger.error("Error claiming device:", error);
      setResult({
        success: false,
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnclaimDevice = async () => {
    if (!selectedDeviceId) {
      setResult({
        success: false,
        message: "Please select a device to unclaim.",
      });
      return;
    }

    // Check typed confirmation matches
    if (typedConfirmation.toLowerCase() !== "unclaim") {
      setResult({
        success: false,
        message: "Please type 'unclaim' to confirm this action.",
      });
      return;
    }

    setLoading(true);

    try {
      // Refresh access token if needed (within 5 minutes of expiry)
      await getAccessToken();

      const response = await fetch(
        `${config.apiEndpoint}/unclaim-device?installationId=${installationId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": config.apiKey,
          },
          body: JSON.stringify({ deviceId: selectedDeviceId }),
        },
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setResult({
          success: true,
          message: `Device unclaimed successfully! Device ID: ${
            data.deviceId || "Unknown"
          }`,
        });
        if (onDevicesClaimed) {
          onDevicesClaimed();
        }
      } else {
        setResult({
          success: false,
          message: data.error || "Failed to unclaim device. Please try again.",
        });
      }
    } catch (error) {
      logger.error("Error unclaiming device:", error);
      setResult({
        success: false,
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setLoading(false);
      setShowConfirmation(false);
      setTypedConfirmation("");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        // Optional: Show a brief "Copied!" message
      })
      .catch((err) => {
        logger.error("Failed to copy:", err);
      });
  };

  const selectedDevice = useMemo(() => {
    return devices?.find((d) => d.deviceId === selectedDeviceId);
  }, [devices, selectedDeviceId]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-content device-claiming-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Initial Choice View */}
        {view === "choice" && !result && (
          <>
            <h2>🫆|🥾 Device Claiming</h2>
            <div className="choice-buttons">
              <button
                className="claim-button"
                onClick={() => setView("claim")}
                disabled={loading}
              >
                🫆 Claim Device
              </button>
              <button
                className="unclaim-button"
                onClick={() => setView("unclaim")}
                disabled={loading}
              >
                🥾 Unclaim Device
              </button>
            </div>
            <div className="modal-actions">
              <button className="cancel-button" onClick={handleClose}>
                Close
              </button>
            </div>
          </>
        )}

        {/* Claim Device View */}
        {view === "claim" && !result && (
          <>
            <h2>🫆 Claim Device</h2>
            <div className="claim-form">
              <label htmlFor="claimToken">
                Paste a Device Claim Token (JWT)
              </label>
              <textarea
                id="claimToken"
                className="claim-token-input"
                value={claimToken}
                onChange={(e) => setClaimToken(e.target.value)}
                placeholder="eyJhbGciOiJFUzI1NiIsInR5cCI6IkpXVCJ9..."
                rows={4}
                disabled={loading}
              />

              {decodedToken && (
                <div className="decoded-token-section">
                  <div className="decoded-header">
                    <label>Decoded Device Claim Token Details</label>
                    {!decodedToken.error && (
                      <button
                        className="copy-button"
                        onClick={() =>
                          copyToClipboard(
                            JSON.stringify(
                              {
                                header: decodedToken.header,
                                payload: decodedToken.payload,
                              },
                              null,
                              2,
                            ),
                          )
                        }
                        title="Copy decoded token"
                      >
                        📋 Copy
                      </button>
                    )}
                  </div>
                  {decodedToken.error ? (
                    <div className="decode-error">{decodedToken.error}</div>
                  ) : (
                    <div className="decoded-token-display">
                      <div className="token-section">
                        <strong>Header:</strong>
                        <SyntaxHighlighter
                          language="json"
                          style={vscDarkPlus}
                          customStyle={{ margin: 0 }}
                        >
                          {JSON.stringify(decodedToken.header, null, 2)}
                        </SyntaxHighlighter>
                      </div>
                      <div className="token-section">
                        <strong>Payload:</strong>
                        <SyntaxHighlighter
                          language="json"
                          style={vscDarkPlus}
                          customStyle={{ margin: 0 }}
                        >
                          {JSON.stringify(decodedToken.payload, null, 2)}
                        </SyntaxHighlighter>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setView("choice")}
                disabled={loading}
              >
                Back
              </button>
              <button
                className="confirm-button"
                onClick={handleClaimDevice}
                disabled={
                  loading ||
                  !claimToken.trim() ||
                  !decodedToken ||
                  decodedToken.error
                }
              >
                {loading ? "Sending..." : "Send Claim Device Request"}
              </button>
            </div>
          </>
        )}

        {/* Unclaim Device View */}
        {view === "unclaim" && !result && !showConfirmation && (
          <>
            <h2>🥾 Unclaim Device</h2>
            <div className="unclaim-form">
              <label htmlFor="deviceSelect">Select Device to Unclaim</label>
              <select
                id="deviceSelect"
                className="device-select"
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                disabled={loading}
              >
                <option value="">-- Select a device --</option>
                {devices?.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.displayName || device.name || "Unnamed Device"} |
                    ID: {device.deviceId} | Serial:{" "}
                    {device.serialNumber || "N/A"}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setView("choice")}
                disabled={loading}
              >
                Back
              </button>
              <button
                className="confirm-button unclaim-button"
                onClick={() => setShowConfirmation(true)}
                disabled={loading || !selectedDeviceId}
              >
                Send Unclaim Device Request
              </button>
            </div>
          </>
        )}

        {/* Unclaim Confirmation Dialog */}
        {view === "unclaim" && !result && showConfirmation && (
          <>
            <h2>⚠️ Confirm Unclaim</h2>
            <div className="confirmation-message">
              <p>
                <strong>Are you sure you want to unclaim this device?</strong>
              </p>
              <p className="device-info">
                Device:{" "}
                <strong>
                  {selectedDevice?.displayName ||
                    selectedDevice?.name ||
                    "Unknown"}
                </strong>
                <br />
                ID: {selectedDevice?.deviceId}
                <br />
                Serial: {selectedDevice?.serialNumber || "N/A"}
              </p>
              <p className="warning-text">
                <strong>Note:</strong> Devices that are unclaimed must be
                Factory Device Reset (FDR'd) and Wi-Fi reconfigured before you
                can reclaim this device to this or another site. You
                MUST be in physical proximity of this device in order to reclaim
                it.
              </p>
            </div>

            <div className="typed-confirmation-section">
              <label>Type "unclaim" to confirm:</label>
              <input
                type="text"
                className="typed-confirmation-input"
                value={typedConfirmation}
                onChange={(e) => setTypedConfirmation(e.target.value)}
                placeholder="unclaim"
                disabled={loading}
              />
            </div>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setShowConfirmation(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="confirm-button warning"
                onClick={handleUnclaimDevice}
                disabled={
                  loading || typedConfirmation.toLowerCase() !== "unclaim"
                }
              >
                {loading ? "Unclaiming..." : "Yes, Unclaim Device"}
              </button>
            </div>
          </>
        )}

        {/* Result View */}
        {result && (
          <>
            <h2>{result.success ? "✅ Success" : "❌ Error"}</h2>
            <div
              className={`result-message ${
                result.success ? "success" : "error"
              }`}
            >
              {result.message}
            </div>
            <div className="modal-actions">
              <button className="cancel-button" onClick={handleClose}>
                Close
              </button>
            </div>
            {result.success && (
              <div className="auto-close-notice">
                This dialog will close automatically in 8 seconds.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeviceClaimingModal;
